<template>
  <div data-private class="h-full flex flex-col">
  <PageHeader
    title="Care Management"
    :heading-level="2"
  >
    <template #bottom>
      <Page_Tabs
        :tabs="tabOptions"
        :initial-tab="currentTab"
        class="px-6 pt-4"
        no-border
        @select="onTabSelect"
      />
    </template>
  </PageHeader>

  <div class="flex-1 overflow-auto min-h-0">
    <!-- Patients Tab -->
    <div v-if="currentTab === 'Patients'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_Patients_List :initial-patient-id="selectedId ? Number(selectedId) : null" />
      </div>
    </div>

    <div v-if="currentTab === 'My Tasks' && myTasksEnabled" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_MyTasks />
      </div>
    </div>

    <!-- My Calendar Tab -->
    <div v-if="currentTab === 'My Calendar'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_MyCalendar />
      </div>
    </div>

    <!-- Overview Tab: SubNav + content -->
    <div v-if="currentTab === 'Overview'" class="flex">
      <div class="cm-subnav">
        <router-link
          v-for="sub in overviewSubPages"
          :key="sub.key"
          :to="overviewLocation(sub.key)"
          class="cm-subnav-btn"
          :class="{ active: overviewSubPage === sub.key }"
        >{{ sub.label }}</router-link>
      </div>
      <div class="flex-1 min-w-0">
        <CareManagement_Operations_IHCI v-if="overviewSubPage === 'operations' && current_tenant_slug === 'ihci'" />
        <CareManagement_DailyOpsMatrix v-else-if="overviewSubPage === 'operations'" />
        <CareManagement_CT_Operations v-if="overviewSubPage === 'ct_operations'" />
        <CareManagement_Outreach_Operations v-if="overviewSubPage === 'outreach_operations'" />
        <CareManagement_ComplexCare_Operations v-if="overviewSubPage === 'complex_care_operations'" />
        <CareManagement_DischargeWaterfall v-if="overviewSubPage === 'discharge_waterfall' && waterfallEnabled" />
        <CareManagement_Pipeline v-if="overviewSubPage === 'pipeline'" />
        <CareManagement_EpisodeGrouper v-if="overviewSubPage === 'episode_grouper'" />
        <MessageDeliveryReport v-if="overviewSubPage === 'delivery_report'" />
      </div>
    </div>

    <!-- Assignments Tab: SubNav + content -->
    <div v-if="currentTab === 'Assignments'" class="flex">
      <div class="cm-subnav">
        <button
          v-for="sub in assignmentSubPages"
          :key="sub.key"
          class="cm-subnav-btn"
          :class="{ active: assignmentSubPage === sub.key }"
          @click="selectAssignmentSub(sub.key)"
        >
          <span>{{ sub.label }}</span>
          <span v-if="sub.badge" class="cm-count-pill warn">{{ sub.badge }}</span>
        </button>
      </div>
      <div class="flex-1 min-w-0">
        <TeamAssignments v-if="assignmentSubPage === 'team'" />
        <CaseloadPanel v-if="assignmentSubPage === 'caseloads'" />
        <AssignmentHealth v-if="assignmentSubPage === 'unassigned'" @health-loaded="onHealthLoaded" />
        <ProgramAssignments v-if="assignmentSubPage === 'programs'" />
        <BulkReassign v-if="assignmentSubPage === 'bulk_reassign'" />
      </div>
    </div>

    <!-- Risk Stratification Tab -->
    <div v-if="currentTab === 'Risk Stratification'" class="p-6">
      <CareManagement_AIRiskScore />
    </div>

    <!-- Search & Enroll Tab -->
    <div v-if="currentTab === 'Search & Enroll'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <CareManagement_SearchEnroll />
      </div>
    </div>

    <!-- Registry Tab. Full-height, self-scrolling: the p-6 wrapper + a
         viewport-height child would stack padding on top of the height and
         overflow, so the Registry owns its own box (h-full) like Provider
         Messaging does. -->
    <div v-if="currentTab === 'Registry'" class="h-full bg-white rounded border border-gray-200">
      <CareManagement_Registry />
    </div>

    <!-- Data Integration Tab -->
    <div v-if="currentTab === 'Data Integration'" class="p-6">
      <div class="bg-white rounded border border-gray-200">
        <PHM_DataIntegration />
      </div>
    </div>

    <!-- QA Tab: per-care-manager call quality scorecards. Manager and above. -->
    <div v-if="currentTab === 'QA'" class="h-full bg-white rounded border border-gray-200">
      <CareManagement_QA :initial-mode="qaMode" :initial-cm-user-id="qaCmUserId" :initial-session-id="qaSessionId" />
    </div>

    <!-- Provider Messaging Tab -->
    <div v-if="currentTab === 'Provider Messaging'" class="h-full bg-white rounded border border-gray-200">
      <ProviderMessagingPage />
    </div>


    <div v-if="currentTab === 'Utilization Mgmt'" class="h-full bg-white rounded border border-gray-200">
      <CareManagement_UM />
    </div>
  </div>

  </div>
</template>

<script>
import '@/assets/cm-tokens.css';
import { mapGetters } from 'vuex'
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import CareManagement_Patients_List from '@/components/Care_Management/CareManagement_Patients_List.vue';
import CareManagement_MyTasks from '@/components/Care_Management/CareManagement_MyTasks.vue';
import CareManagement_Operations_IHCI from '@/components/Care_Management/CareManagement_Operations_IHCI.vue';
import CareManagement_CT_Operations from '@/components/Care_Management/CareManagement_CT_Operations.vue';
import CareManagement_Outreach_Operations from '@/components/Care_Management/CareManagement_Outreach_Operations.vue';
import CareManagement_ComplexCare_Operations from '@/components/Care_Management/CareManagement_ComplexCare_Operations.vue';
import CareManagement_DailyOpsMatrix from '@/components/Care_Management/CareManagement_DailyOpsMatrix.vue';
import CareManagement_DischargeWaterfall from '@/components/Care_Management/CareManagement_DischargeWaterfall.vue';
import CareManagement_Pipeline from '@/components/Care_Management/CareManagement_Pipeline.vue';
import CareManagement_EpisodeGrouper from '@/components/Care_Management/CareManagement_EpisodeGrouper.vue';

// Tenants with a Discharge Waterfall adapter DA + worklist configured. MUST stay
// in step with WATERFALL_WORKLIST_BY_TENANT in discharge_waterfall_service.py —
// the backend raises for an unconfigured tenant rather than falling back to
// another tenant's data, so listing one here without the other is a hard error.
const WATERFALL_TENANTS = ['ihci', 'vytalize'];
const MY_TASKS_TENANTS = ['ihci', 'vytalize'];
import CareManagement_AIRiskScore from '@/components/Care_Management/CareManagement_AIRiskScore.vue';
import CareManagement_MyCalendar from '@/components/Care_Management/CareManagement_MyCalendar.vue';
import CareManagement_SearchEnroll from '@/components/Care_Management/CareManagement_SearchEnroll.vue';
import CareManagement_Registry from '@/components/Care_Management/CareManagement_Registry.vue';
import PHM_DataIntegration from '@/components/PHM/PHM_DataIntegration.vue';
import TeamAssignments from '@/components/Care_Management/TeamAssignments.vue';
import CaseloadPanel from '@/components/Care_Management/CaseloadPanel.vue';
import AssignmentHealth from '@/components/Care_Management/AssignmentHealth.vue';
import ProgramAssignments from '@/components/Care_Management/ProgramAssignments.vue';
import BulkReassign from '@/components/Care_Management/BulkReassign.vue';
import ProviderMessagingPage from '@/components/Care_Management/ProviderMessagingPage.vue';
import MessageDeliveryReport from '@/components/Care_Management/MessageDeliveryReport.vue';
import CareManagement_QA from '@/components/Care_Management/CareManagement_QA.vue';
import CareManagement_UM from '@/components/Care_Management/CareManagement_UM.vue';

import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import { overviewLocation, overviewSectionKey } from '../../../router/caremanagement_routes';

export default {
  name: 'CareManagementOverview',
  components: {
    CareManagement_Patients_List,
    CareManagement_MyTasks,
    CareManagement_Operations_IHCI,
    CareManagement_CT_Operations,
    CareManagement_Outreach_Operations,
    CareManagement_ComplexCare_Operations,
    CareManagement_DailyOpsMatrix,
    CareManagement_DischargeWaterfall,
    CareManagement_Pipeline,
    CareManagement_EpisodeGrouper,
    CareManagement_AIRiskScore,
    CareManagement_MyCalendar,
    CareManagement_SearchEnroll,
    CareManagement_Registry,
    PHM_DataIntegration,
    TeamAssignments,
    CaseloadPanel,
    AssignmentHealth,
    ProgramAssignments,
    BulkReassign,
    ProviderMessagingPage,
    MessageDeliveryReport,
    CareManagement_QA,
    CareManagement_UM,
    PageHeader,
    Page_Tabs,
  },
  props: {
    activeTab: { type: String, default: 'Patients' },
    selectedId: { type: [String, Number], default: null },
    qaMode: { type: String, default: 'report' },
    qaCmUserId: { type: Number, default: null },
    qaSessionId: { type: String, default: null },
  },
  data() {
    return {
      currentTab: this.activeTab,
      assignmentSubPage: 'team',
      assignmentSubPages: [
        { key: 'team', label: 'Team Assignments' },
        { key: 'caseloads', label: 'Caseloads' },
        { key: 'unassigned', label: 'Unassigned Tasks', badge: null },
        { key: 'programs', label: 'Program Assignments' },
        { key: 'bulk_reassign', label: 'Bulk Reassign' },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug', 'isManagerViewer', 'isTenantAdmin']),
    waterfallEnabled() { return WATERFALL_TENANTS.includes(this.current_tenant_slug); },
    myTasksEnabled() { return MY_TASKS_TENANTS.includes(this.current_tenant_slug); },
    overviewSubPages() {
      // Daily Operations: for Vytalize this is the merged role×tier matrix view
      // (the old flat "Daily Operations" + "Daily Ops Overview" were combined
      // into one — Chris Weigert, 2026-07-20). IHCI keeps its own operations view.
      const subs = [
        { key: 'operations', label: 'Daily Operations' },
      ];
      if (this.current_tenant_slug === 'ihci') {
        subs.push({ key: 'ct_operations', label: 'Care Transitions Operations' });
        subs.push({ key: 'outreach_operations', label: 'Outreach Operations' });
        subs.push({ key: 'complex_care_operations', label: 'Complex Care Operations' });
      }
      // Discharge Waterfall: the post-discharge funnel report (Nesha/Rachel
      // 2026-07-22 spec). Per-tenant, gated on having a waterfall worklist +
      // adapter DA configured — see WATERFALL_WORKLIST_BY_TENANT in
      // discharge_waterfall_service.py. Keep this list in step with that dict:
      // a tenant listed here but not there gets a hard 500 from the endpoint.
      if (WATERFALL_TENANTS.includes(this.current_tenant_slug)) {
        subs.push({ key: 'discharge_waterfall', label: 'Discharge Waterfall' });
      }
      subs.push({ key: 'pipeline', label: 'Pipeline' });
      // Episode Grouper: algorithmic ADT_EPISODES vs the nightly LLM encounter
      // resolver. Open to every CM role — the people who adjudicate these
      // disagreements (the operational leads who report the episode-grouping
      // bugs) hold `standard`, and gating this manager+ locked out its intended
      // readers. Read-only; no PHI beyond the MBI already on every CM screen.
      // Backend access_roles on EpisodeGrouperComparisonView is the real gate.
      subs.push({ key: 'episode_grouper', label: 'Episode Grouper' });
      if (canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false })) {
        subs.push({ key: 'delivery_report', label: 'Messaging Delivery Report' });
      }
      // 'program_effect' was a scratch table of completed opportunities with the
      // care-graph loop flags. It is now the episode drilldown INSIDE Discharge
      // Waterfall (click any statistic), sourced from the same DA as the
      // aggregates so the list and the numbers cannot disagree.
      return subs;
    },
    // The Overview sub-page comes from the path (/caremanagement/overview/<section>).
    // An unknown or gated section falls back to Daily Operations.
    overviewSubPage() {
      const key = overviewSectionKey(this.$route.params.section);
      return this.overviewSubPages.some(s => s.key === key) ? key : 'operations';
    },
    tabOptions() {
      const tabs = [
        { label: 'Overview', icon: ['fas', 'chart-bar'] },
        { label: 'Patients', icon: ['fas', 'users'] },
      ];
      if (this.myTasksEnabled) {
        tabs.push({ label: 'My Tasks', icon: ['fas', 'list-check'] });
      }
      if (this.current_tenant_slug !== 'ihci') {
        tabs.push({ label: 'My Calendar', icon: ['fas', 'calendar-check'] });
      }
      tabs.push(
        { label: 'Assignments', icon: ['fas', 'users-cog'] },
        { label: 'Search & Enroll', icon: ['fas', 'search'] },
        { label: 'Registry', icon: ['fas', 'layer-group'] },
      );
      if (canAccessFeature(FEATURES.MODULE_RISK_STRATIFICATION)) {
        tabs.push({ label: 'Risk Stratification', icon: ['fas', 'layer-group'] });
      }
      if (canAccessFeature(FEATURES.MODULE_MANAGE)) {
        tabs.push({ label: 'Utilization Mgmt', icon: ['fas', 'hospital'] });
      }
      if (this.current_user?.is_superuser) {
        tabs.push({ label: 'Data Integration', icon: ['fas', 'table'] });
      }
      // QA is one surface with role-adaptive visibility: standard users see
      // their own scores; managers/admins also get the audit queue and team
      // rollups. The backend enforces the same split.
      tabs.push({ label: 'QA', icon: ['fas', 'clipboard-check'] });
      if (canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false })) {
        tabs.push({ label: 'Provider Messaging', icon: ['fas', 'envelope'] })
      }
      return tabs
    },
  },
  watch: {
    activeTab(val) {
      this.currentTab = this.guardTab(val);
    },
    // Deep-linking / back-forward: keep the subtab in sync with the hash.
    '$route.hash'(hash) {
      this.applyHashSubPage(hash);
    },
  },
  created() {
    // Direct URL hit on /caremanagement/tasks by a tenant without My Tasks would render
    // a blank body (tab gated below). Bounce them to Patients and fix the URL.
    // NOTE: this guard lives here (not in data()) because methods/getters are
    // not yet bound to `this` when data() runs — calling guardTab() from data()
    // throws "guardTab is not a function".
    this.currentTab = this.guardTab(this.activeTab);
    if (this.activeTab === 'My Tasks' && !this.myTasksEnabled) {
      this.$router.replace('/caremanagement/patients').catch(() => {});
    }
    if (this.activeTab === 'My Calendar' && this.current_tenant_slug === 'ihci') {
      this.$router.replace({ path: '/caremanagement/tasks', hash: '#month' }).catch(() => {});
    }
    // Restore the active subtab from the URL hash (#operations, #team, ...).
    // The parent tab lives in the path; the subtab lives in the hash. An
    // unknown/ungated hash falls through to the group's default.
    this.applyHashSubPage(this.$route.hash);
  },
  methods: {
    // Strip the leading '#' and return the subtab key, or '' if none.
    _hashKey(hash) {
      return (hash || '').replace(/^#/, '');
    },
    // Point the subtab state at the hash key when it's valid for the current
    // tab; otherwise leave the default in place.
    applyHashSubPage(hash) {
      const key = this._hashKey(hash);
      if (!key) return;
      if (this.currentTab === 'Assignments'
          && this.assignmentSubPages.some(s => s.key === key)) {
        this.assignmentSubPage = key;
      }
    },
    overviewLocation,
    selectAssignmentSub(key) {
      this.assignmentSubPage = key;
      this._setHash(key);
    },
    _setHash(key) {
      if (this._hashKey(this.$route.hash) === key) return;
      this.$router.replace({ hash: `#${key}` }).catch(() => {});
    },
    guardTab(tab) {
      if (tab === 'My Tasks' && !this.myTasksEnabled) {
        return 'Patients';
      }
      if (tab === 'My Calendar' && this.current_tenant_slug === 'ihci') {
        return 'My Tasks';
      }
      return tab;
    },
    onTabSelect(tab) {
      this.currentTab = tab;
      if (tab === 'Overview') {
        this.$router.push(overviewLocation('operations')).catch(() => {});
      } else if (tab === 'My Calendar') {
        this.$router.replace('/caremanagement/calendar').catch(() => {});
      } else if (tab === 'Patients') {
        this.$router.replace('/caremanagement/patients').catch(() => {});
      } else if (tab === 'My Tasks') {
        this.$router.replace('/caremanagement/tasks').catch(() => {});
      } else if (tab === 'Assignments') {
        this.$router.replace({ path: '/caremanagement/assignments', hash: `#${this.assignmentSubPage}` }).catch(() => {});
      } else if (tab === 'Risk Stratification') {
        this.$router.replace('/caremanagement/risk').catch(() => {});
      } else if (tab === 'Search & Enroll') {
        this.$router.replace('/caremanagement/search-enroll').catch(() => {});
      } else if (tab === 'Registry') {
        this.$router.replace('/caremanagement/registry').catch(() => {});
      } else if (tab === 'Data Integration') {
        this.$router.replace('/caremanagement/data-integration').catch(() => {});
      } else if (tab === 'Provider Messaging') {
        this.$router.replace('/caremanagement/provider-messaging').catch(() => {});
      } else if (tab === 'Utilization Mgmt') {
        this.$router.replace('/caremanagement/utilization-management').catch(() => {});
      } else if (tab === 'QA') {
        this.$router.replace('/caremanagement/qa/report').catch(() => {});
      }
    },
    onHealthLoaded(data) {
      const sub = this.assignmentSubPages.find(s => s.key === 'unassigned');
      if (sub) sub.badge = data?.total_unassigned || null;
    },
  },
};
</script>
